import React, { useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { Link } from 'expo-router';
import { landingPalette } from '@/components/Landing/landingTokens';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import MacDownloadProvider, { useMacDownload, useMacDownloadDetails } from '@/components/Mac/MacDownloadProvider';

/** Mac distribution is direct; other surfaces link to the Mac feature page. */
export default function MacAppButton({ download = false, scale = 1, featured = false, onNavigate }) {
  const button = <MacButton download={download} scale={scale} featured={featured} onNavigate={onNavigate} />;
  return download ? <MacDownloadProvider>{button}</MacDownloadProvider> : button;
}

function MacButton({ download, scale, featured, onNavigate }) {
  const { theme } = useTheme();
  const c = landingPalette(isDarkTheme(theme));
  const foreground = download ? c.onAccent : '#FFFFFF';
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const details = useMacDownloadDetails();
  const showSetup = useMacDownload();
  const url = download ? details?.url : null;
  const href = download ? url || '/mac' : '/mac';
  const label = download ? url ? 'Download for Mac' : 'Explore Mac setup' : 'Gab AI for Mac';
  const fileLink = Platform.OS === 'web' && Boolean(url);
  // Link's Slot needs a plain style object, not a Pressable style callback.
  const button = <Pressable accessibilityRole={href ? 'link' : 'button'}
    accessibilityLabel={url ? 'Download Gab AI for Mac' : 'Explore Gab AI for Mac'}
    onPress={url ? showSetup : onNavigate}
    {...(fileLink ? { href, hrefAttrs: { download: true, target: '_blank', rel: 'noopener noreferrer' } } : {})}
    accessibilityState={{ disabled: !href }} disabled={!href}
    onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)}
    onPressIn={() => setPressed(true)} onPressOut={() => setPressed(false)}
    style={{
      minHeight: featured ? Math.round(82 * scale) : download ? 54 : Math.round(47 * scale),
      ...(featured ? { width: Math.round(330 * scale), maxWidth: '100%' } : {}),
      paddingHorizontal: featured ? 24 : download ? 26 : 14,
      paddingVertical: featured ? 16 : 10, borderRadius: featured ? 13 : download ? 10 : 7,
      flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: featured ? 16 : 10,
      backgroundColor: download ? c.accentFill : '#040404',
      borderWidth: 1, borderColor: download ? c.accentFill : '#A6A6A6',
      opacity: href && (hovered || pressed) ? 0.82 : 1,
    }}>
    <Icon name={download ? 'download' : 'desktop'} size={featured ? Math.round(32 * scale) : download ? 17 : 22} color={foreground} />
    <View style={{ gap: 5, flexShrink: 1 }}>
      <Text weight="medium" style={{ color: foreground, fontSize: featured ? Math.round(23 * scale) : download ? 17 : 14 }}>{label}</Text>
      {featured && <Text style={{ color: foreground, fontSize: Math.round(12 * scale) }}>Your AI workspace on macOS</Text>}
    </View>
    {!download && <Icon name="arrow-right" size={featured ? 15 : 11} color={foreground} />}
  </Pressable>;

  return <View style={{ alignItems: 'center', gap: 10 }}>
    {fileLink ? button : <Link href={href} {...(url ? { target: '_blank', rel: 'noopener noreferrer' } : {})} asChild>{button}</Link>}
    {download && <Text size="s" color="secondary" style={{ textAlign: 'center' }}>
      {url ? details?.release ? `Version ${details.release.version} (${details.release.build})${details.local ? ' · Local preview' : ''}` : 'Direct download' : 'Setup and installation guide'}
    </Text>}
  </View>;
}
